/**
 * Shared label + dot-colour map for `professional_status` and
 * `contact_availability` so every messenger surface (thread header,
 * floating chat header, network sidebar, profile drawer) renders the
 * same value consistently.
 *
 * Keys MUST match the backend whitelist in account.py.
 * `available` / `open` are treated as the no-explicit-status baselines
 * by callers (no badge rendered when the value matches the baseline).
 */
const PROF_STATUS_MAP = {
  available:   { label: 'Available',   dot: 'bg-green-500'  },
  online:      { label: 'Online',      dot: 'bg-green-500'  },
  offline:     { label: 'Offline',     dot: 'bg-gray-400'   },
  auditioning: { label: 'Auditioning', dot: 'bg-blue-500'   },
  rehearsing:  { label: 'Rehearsing',  dot: 'bg-purple-500' },
  on_set:      { label: 'On Set',      dot: 'bg-red-500'    },
  in_training: { label: 'In Training', dot: 'bg-indigo-500' },
  unavailable: { label: 'Unavailable', dot: 'bg-gray-500'   },
};

const CONTACT_AVAIL_MAP = {
  open:        { label: 'Open to Contact',     dot: 'bg-emerald-500', tone: 'text-emerald-600' },
  limited:     { label: 'Limited Availability', dot: 'bg-amber-500',   tone: 'text-amber-600' },
  unavailable: { label: 'Not Available',        dot: 'bg-rose-500',    tone: 'text-rose-600' },
};

export function professionalStatusMeta(value) {
  if (!value) return null;
  return PROF_STATUS_MAP[value] || null;
}

/**
 * `available` is the silent baseline — every new user is bucketed
 * there by default, so rendering "Available" on every thread would
 * be visual noise. Callers ask `shouldShowProfessionalStatus(value)`
 * and we return false for null / unknown / 'available'.
 */
export function shouldShowProfessionalStatus(value) {
  if (!value) return false;
  if (value === 'available') return false;
  return !!PROF_STATUS_MAP[value];
}

export function contactAvailabilityMeta(value) {
  if (!value) return null;
  return CONTACT_AVAIL_MAP[value] || null;
}

/**
 * `open` is the silent baseline for contact availability — most users
 * are open by default, so showing the badge everywhere would be
 * noisy. Only `limited` and `unavailable` render a pill, because
 * those are the states where viewers genuinely need to know "this
 * person may not respond / is closed to new contact right now".
 */
export function shouldShowContactAvailability(value) {
  if (!value) return false;
  if (value === 'open') return false;
  return !!CONTACT_AVAIL_MAP[value];
}
